import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { EMPTY_THUMBNAIL_STRIPE_GRADIENT } from '../../../constants/ui';
import { HorizontalCardVariantEnum } from '../../../types/shell';
import { isActivationKey } from '../../../util/ui';
import LightboxBackgroundImage from './LightboxBackgroundImage';

interface IProps {
  image_url?: string;
  name: string;
  body?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  onThumbnailClick?: () => void;
  thumbnailAriaLabel?: string;
  sx?: SxProps<Theme>;
  variant?: HorizontalCardVariantEnum;
  showCheckmarkBadge?: boolean;
  isThumbnailEmpty?: boolean;
  thumbnailFit?: 'cover' | 'contain';
  hasThumbnailBorder?: boolean;
  isDisabled?: boolean;
  hideDisabledLabel?: boolean;
  hasHoverEffect?: boolean;
  isSoldOut?: boolean;
}

export const HorizontalCard: React.FC<IProps> = ({
  image_url,
  name,
  body,
  children,
  onClick,
  ariaLabel,
  onThumbnailClick,
  thumbnailAriaLabel,
  sx,
  variant = HorizontalCardVariantEnum.Default,
  showCheckmarkBadge = false,
  isThumbnailEmpty = false,
  thumbnailFit = 'contain',
  hasThumbnailBorder = false,
  isDisabled = false,
  hideDisabledLabel = false,
  hasHoverEffect = false,
  isSoldOut = false,
}) => {
  const theme = useTheme();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!onClick || event.target !== event.currentTarget || !isActivationKey(event)) {
        return;
      }
      event.preventDefault();
      onClick();
    },
    [onClick],
  );

  const handleThumbnailClick = useCallback(
    (event: React.MouseEvent) => {
      if (!onThumbnailClick) {
        return;
      }
      event.stopPropagation();
      onThumbnailClick();
    },
    [onThumbnailClick],
  );

  const cardStyles = useMemo(
    () =>
      ({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 1.5,
        px: 2,
        py: variant === HorizontalCardVariantEnum.Default ? 1.25 : 1.875,
        flex: 1,
        minWidth: 0,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        ...(onClick && { cursor: 'pointer' }),
        ...(hasHoverEffect && {
          '& .hover-children': {
            transition: 'opacity 0.2s ease-in-out',
            '@media (hover: hover)': {
              opacity: 0,
            },
          },
          '@media (hover: hover)': {
            '&:hover .hover-children': {
              opacity: 1,
            },
          },
        }),
        ...sx,
      } as SxProps<Theme>),
    [variant, hasHoverEffect, onClick, sx, theme],
  );

  const imageContainerStyles = useMemo(
    () => ({
      boxSizing: 'border-box',
      width: 56,
      height: 56,
      minWidth: 56,
      minHeight: 56,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: onThumbnailClick ? 'pointer' : 'inherit',
      borderRadius: 1,
      overflow: 'hidden',
      backgroundColor: theme.palette.common.white,
      ...(hasThumbnailBorder && { border: `1px solid ${theme.palette.divider}` }),
      ...(isThumbnailEmpty && { background: EMPTY_THUMBNAIL_STRIPE_GRADIENT, cursor: 'default' }),
      ...(isSoldOut && { filter: 'grayscale(1)' }),
    }),
    [theme, isThumbnailEmpty, hasThumbnailBorder, isSoldOut, onThumbnailClick],
  );

  const outOfStockLabel = hideDisabledLabel ? null : (
    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
      Out of stock
    </Typography>
  );

  return (
    <Box
      sx={cardStyles}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? ariaLabel : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
    >
      {/* Non-clipping wrapper so the badge can overhang; image box keeps overflow:hidden for its rounded edge. */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box
          sx={imageContainerStyles}
          role={onThumbnailClick ? 'button' : undefined}
          aria-label={onThumbnailClick ? thumbnailAriaLabel : undefined}
          onClick={onThumbnailClick ? handleThumbnailClick : undefined}
        >
          {!isThumbnailEmpty && (
            <LightboxBackgroundImage
              url={image_url ?? ''}
              asImage={true}
              isScalable={false}
              sx={{ width: '100%', height: '100%', objectFit: thumbnailFit }}
            />
          )}
        </Box>
        {showCheckmarkBadge && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -6,
              right: -6,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minWidth: 0,
            flex: '1 1 0%',
          }}
        >
          <Typography
            variant="body1"
            fontWeight="medium"
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...(isSoldOut && { color: 'text.secondary', textDecoration: 'line-through' }),
            }}
          >
            {name}
          </Typography>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              minWidth: 0,
            }}
          >
            {body}
            {isDisabled && outOfStockLabel}
          </Box>
        </Box>
        <Box
          className="hover-children"
          sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', flexShrink: 0 }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
